import React, { useState, useEffect } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  ActionIcon,
  Paper,
  Badge,
  Box,
  Alert,
} from "@mantine/core";
import {
  IconCheck,
  IconPlus,
  IconGlobe,
  IconAlertCircle,
} from "@tabler/icons-react";
import { useTemplate } from "../../../context/TemplateContext";
import { useSalon } from "../../../context/SalonContext";
import { BusinessInfo } from "../../../types/context";
import moment from "moment";

const COLOR_BORDER_LIGHT = "#e9ecef";
const COLOR_BORDER_SELECTED = "#1976d2";
const COLOR_BACKGROUND_LIGHT = "#f8f9fa";
const COLOR_BACKGROUND_SELECTED = "#e3f2fd";
const COLOR_ICON_GRAY = "#6c757d";
const COLOR_SUCCESS_BACKGROUND = "#e8f5e8";
const COLOR_SUCCESS_BORDER = "#4caf50";

interface WebsiteGeneratorPublishModalProps {
  isOpen: boolean;
  onClose: () => void;
  hasCustomDomain?: boolean;
  onPublishSuccess?: () => void;
}

const WebsiteGeneratorPublishModal: React.FC<
  WebsiteGeneratorPublishModalProps
> = ({ isOpen, onClose, hasCustomDomain = false, onPublishSuccess }) => {
  const {
    rebuildWebsite,
    loadPreviewData,
    getCurrentSubdomain,
    listAllDomains,
    getDeployStatus,
  } = useTemplate();
  const { businessInfo } = useSalon() as { businessInfo?: BusinessInfo | null };
  const salonId = businessInfo?.id || "";

  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [publishSuccess, setPublishSuccess] = useState<string | null>(null);
  const [lastPublishTime, setLastPublishTime] = useState<string | null>(null);
  const [subdomainUrl, setSubdomainUrl] = useState<string | null>(null);
  const [customDomainUrl, setCustomDomainUrl] = useState<string | null>(null);

  // Format date for display
  const formatLastPublished = (
    isoTimestamp: string | null | undefined,
  ): string => {
    if (!isoTimestamp) return "Never";
    const date = moment(isoTimestamp);
    if (!date.isValid()) return "Never";
    return date.format("M/D/YYYY h:mm A");
  };

  // Fetch preview data, subdomain, and custom domain when modal opens
  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen || !salonId) return;

      try {
        // Fetch last published time
        const data = await loadPreviewData(salonId);
        if (data?.metadata?.last_published_at) {
          setLastPublishTime(
            formatLastPublished(data.metadata.last_published_at),
          );
        } else {
          setLastPublishTime("Never");
        }

        // Fetch current subdomain
        try {
          const subdomainResponse = await getCurrentSubdomain(salonId, "live");
          if (subdomainResponse?.full_domain) {
            setSubdomainUrl(subdomainResponse.full_domain);
          }
        } catch (error) {
          console.error("Error fetching subdomain:", error);
          // Fallback to generated subdomain if API fails
          const fallbackSubdomain =
            businessInfo?.salon_name
              ?.replace(/[^a-zA-Z0-9]/g, "")
              .toLowerCase() || "yourbusiness";
          setSubdomainUrl(`${fallbackSubdomain}.pagoda.site`);
        }

        // Fetch active custom domain
        try {
          const domainsResponse = await listAllDomains(salonId);
          if (domainsResponse?.domains && domainsResponse.domains.length > 0) {
            // Find the first active custom domain
            const activeDomain = domainsResponse.domains.find(
              (domain) => domain.status === "active",
            );
            if (activeDomain) {
              // Verify it's actually active by checking deploy status
              try {
                const deployStatus = await getDeployStatus(
                  salonId,
                  activeDomain.custom_domain,
                );
                if (
                  deployStatus?.status === "active" &&
                  deployStatus?.custom_domain
                ) {
                  setCustomDomainUrl(deployStatus.custom_domain);
                }
              } catch (error) {
                console.error("Error fetching deploy status:", error);
              }
            }
          }
        } catch (error) {
          console.error("Error fetching custom domains:", error);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setLastPublishTime("Never");
      }
    };

    fetchData();
  }, [
    isOpen,
    salonId,
    loadPreviewData,
    getCurrentSubdomain,
    listAllDomains,
    getDeployStatus,
    businessInfo?.salon_name,
  ]);

  const handlePublish = async (domainType: string) => {
    if (!salonId) {
      setPublishError("Salon ID is missing. Please try again.");
      return;
    }

    setIsPublishing(true);
    setPublishError(null);
    setPublishSuccess(null);

    try {
      const response = await rebuildWebsite(salonId);

      // Update last publish time to current time (optimistic update)
      setLastPublishTime(formatLastPublished(new Date().toISOString()));

      // Call success callback to trigger parent to show success modal
      if (onPublishSuccess) {
        onPublishSuccess();
      } else {
        // Fallback: show inline success message and close after delay
        const successMessage = response.cache_invalidation_id
          ? `Website published successfully! Cache invalidation ID: ${response.cache_invalidation_id}`
          : "Website published successfully!";
        setPublishSuccess(successMessage);

        setTimeout(() => {
          onClose();
          setPublishSuccess(null);
        }, 2000);
      }
    } catch (error: any) {
      console.error("Publishing error:", error);
      const errorMessage =
        error?.message || "Failed to publish website. Please try again.";
      setPublishError(errorMessage);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleAddDomain = () => {
    // Navigate to settings/website page with URL tab
    onClose();
    window.location.href = "/admin/settings/website?tab=url";
  };

  const handleClose = () => {
    setPublishError(null);
    setPublishSuccess(null);
    onClose();
  };

  return (
    <Modal
      opened={isOpen}
      onClose={handleClose}
      size="md"
      title={
        <Text fw={600} size="lg">
          Publish Website
        </Text>
      }
    >
      <Stack gap="md">
        {/* Pagoda.site Domain */}
        <Paper
          p="md"
          style={{
            border: hasCustomDomain
              ? `1px solid ${COLOR_BORDER_LIGHT}`
              : `2px solid ${COLOR_BORDER_SELECTED}`,
            borderRadius: "8px",
            backgroundColor: hasCustomDomain
              ? COLOR_BACKGROUND_LIGHT
              : COLOR_BACKGROUND_SELECTED,
          }}
        >
          <Group justify="space-between" align="center" mb="sm">
            <Group gap="sm">
              <IconGlobe size={20} color={COLOR_BORDER_SELECTED} />
              <Box>
                <Text fw={500} size="sm">
                  pagoda.site
                </Text>
                <Text size="xs" c="dimmed">
                  {subdomainUrl ||
                    (businessInfo?.salon_name
                      ?.replace(/[^a-zA-Z0-9]/g, "")
                      .toLowerCase() || "yourbusiness") + ".pagoda.site"}
                </Text>
              </Box>
            </Group>
            {!hasCustomDomain && (
              <Badge color="blue" variant="light">
                Selected
              </Badge>
            )}
          </Group>

          <Group justify="space-between" align="center">
            <Text size="xs" c="dimmed">
              Last published: {lastPublishTime ?? "Never"}
            </Text>
            <Button
              size="sm"
              onClick={() => handlePublish("pagoda.site")}
              loading={isPublishing}
              disabled={isPublishing || !salonId}
            >
              Publish
            </Button>
          </Group>
        </Paper>

        {/* Custom Domain */}
        {hasCustomDomain ? (
          <Paper
            p="md"
            style={{
              border: `2px solid ${COLOR_BORDER_SELECTED}`,
              borderRadius: "8px",
              backgroundColor: COLOR_BACKGROUND_SELECTED,
            }}
          >
            <Group justify="space-between" align="center" mb="sm">
              <Group gap="sm">
                <IconGlobe size={20} color={COLOR_BORDER_SELECTED} />
                <Box>
                  <Text fw={500} size="sm">
                    Custom Domain
                  </Text>
                  <Text size="xs" c="dimmed">
                    {customDomainUrl || "yourbusiness.com"}
                  </Text>
                </Box>
              </Group>
              <Badge color="blue" variant="light">
                Selected
              </Badge>
            </Group>

            <Group justify="space-between" align="center">
              <Text size="xs" c="dimmed">
                Last published: {lastPublishTime || "Never"}
              </Text>
              <Button
                size="sm"
                onClick={() => handlePublish("custom")}
                loading={isPublishing}
                disabled={isPublishing || !salonId}
              >
                Publish
              </Button>
            </Group>
          </Paper>
        ) : (
          <Paper
            p="md"
            style={{
              border: `1px solid ${COLOR_BORDER_LIGHT}`,
              borderRadius: "8px",
              backgroundColor: COLOR_BACKGROUND_LIGHT,
            }}
          >
            <Group justify="space-between" align="center">
              <Group gap="sm">
                <IconPlus size={20} color={COLOR_ICON_GRAY} />
                <Box>
                  <Text fw={500} size="sm">
                    Add Custom Domain
                  </Text>
                  <Text size="xs" c="dimmed">
                    Use your own domain name
                  </Text>
                </Box>
              </Group>
              <Button variant="outline" size="sm" onClick={handleAddDomain}>
                Add Domain
              </Button>
            </Group>
          </Paper>
        )}

        {/* Error Message */}
        {publishError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            <Text fw={600} mb="xs">
              Publishing Failed
            </Text>
            <Text size="sm">{publishError}</Text>
          </Alert>
        )}

        {/* Success Message */}
        {publishSuccess && (
          <Alert icon={<IconCheck size={16} />} color="green">
            <Text fw={600} mb="xs">
              Published Successfully!
            </Text>
            <Text size="sm">{publishSuccess}</Text>
          </Alert>
        )}

        {/* Publishing Status */}
        {isPublishing && (
          <Paper
            p="md"
            style={{
              backgroundColor: COLOR_SUCCESS_BACKGROUND,
              border: `1px solid ${COLOR_SUCCESS_BORDER}`,
              borderRadius: "8px",
            }}
          >
            <Group gap="sm">
              <ActionIcon variant="light" color="green">
                <IconCheck size={16} />
              </ActionIcon>
              <Text size="sm" c="green" fw={500}>
                Publishing your website... This usually takes 5-10 seconds.
              </Text>
            </Group>
          </Paper>
        )}
      </Stack>
    </Modal>
  );
};

export default WebsiteGeneratorPublishModal;
